import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";
import "./AdminBankingDetails.css";

const EMPTY_FORM = {
  bank_name:       "",
  account_name:    "",
  account_number:  "",
  branch_code:     "",
  account_type:    "",
  reference_note:  "Use your order reference number",
  is_active:       1,
  sort_order:      0,
};

/* ─────────────────────────────────
   Dot-menu
   ───────────────────────────────── */
function DotMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="abd-dot-menu" ref={ref}>
      <button
        className="abd-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="abd-dot-menu__dropdown">
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="abd-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="abd-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="abd-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status Badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`abd-badge ${isActive ? "abd-badge--active" : "abd-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, record, onClose, onSaved }) {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    if (mode === "edit" && record) {
      setForm({
        bank_name:      record.bank_name      || "",
        account_name:   record.account_name   || "",
        account_number: record.account_number || "",
        branch_code:    record.branch_code    || "",
        account_type:   record.account_type   || "",
        reference_note: record.reference_note || "Use your order reference number",
        is_active:      Number(record.is_active) === 1 ? 1 : 0,
        sort_order:     record.sort_order     ?? 0,
      });
    }
  }, [mode, record]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    setError("");
    if (!form.bank_name.trim())      return setError("Bank name is required.");
    if (!form.account_name.trim())   return setError("Account name is required.");
    if (!form.account_number.trim()) return setError("Account number is required.");

    setSaving(true);
    try {
      const payload = {
        bank_name:      form.bank_name.trim(),
        account_name:   form.account_name.trim(),
        account_number: form.account_number.trim(),
        branch_code:    form.branch_code.trim()    || null,
        account_type:   form.account_type.trim()   || null,
        reference_note: form.reference_note.trim() || null,
        is_active:      form.is_active,
        sort_order:     parseInt(form.sort_order)  || 0,
      };

      if (mode === "create") {
        await apiFetch("/api/admin/banking-details", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(payload),
        });
      } else {
        await apiFetch(`/api/admin/banking-details/${record.id}`, {
          method:  "PUT",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="abd-modal-overlay" onClick={onClose}>
      <div className="abd-modal abd-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="abd-modal__header">
          <div className="abd-modal__title-row">
            <span className="abd-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="abd-modal__title">
              {mode === "create" ? "Add Bank Account" : "Edit Bank Account"}
            </h2>
          </div>
          <button className="abd-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="abd-modal__body abd-modal__body--form">
          {error && <div className="abd-modal__error">⚠ {error}</div>}

          {/* ── Bank Info ── */}
          <div className="abd-form-section">
            <h3 className="abd-form-section__title">Bank Information</h3>
            <div className="abd-form-grid abd-form-grid--2col">
              <div className="abd-form-field abd-form-field--full">
                <label>Bank Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.bank_name}
                  onChange={(e) => set("bank_name", e.target.value)}
                  placeholder="e.g. First National Bank"
                />
              </div>
              <div className="abd-form-field">
                <label>Account Holder Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.account_name}
                  onChange={(e) => set("account_name", e.target.value)}
                  placeholder="e.g. Noel Auto Parts (Pty) Ltd"
                />
              </div>
              <div className="abd-form-field">
                <label>Account Number <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.account_number}
                  onChange={(e) => set("account_number", e.target.value)}
                  placeholder="e.g. 62012345678"
                />
              </div>
              <div className="abd-form-field">
                <label>Branch Code</label>
                <input
                  type="text"
                  value={form.branch_code}
                  onChange={(e) => set("branch_code", e.target.value)}
                  placeholder="e.g. 250655"
                />
              </div>
              <div className="abd-form-field">
                <label>Account Type</label>
                <input
                  type="text"
                  value={form.account_type}
                  onChange={(e) => set("account_type", e.target.value)}
                  placeholder="e.g. Cheque / Current / Savings"
                />
              </div>
            </div>
          </div>

          {/* ── Payment Reference ── */}
          <div className="abd-form-section">
            <h3 className="abd-form-section__title">Payment Reference Note</h3>
            <div className="abd-form-grid">
              <div className="abd-form-field abd-form-field--full">
                <label>Reference Instruction</label>
                <input
                  type="text"
                  value={form.reference_note}
                  onChange={(e) => set("reference_note", e.target.value)}
                  placeholder="e.g. Use your order reference number"
                />
                <p className="abd-form-hint">
                  Shown to customers on the checkout page so they know what to use as a payment reference.
                </p>
              </div>
            </div>
          </div>

          {/* ── Options ── */}
          <div className="abd-form-section">
            <h3 className="abd-form-section__title">Options</h3>
            <div className="abd-form-grid abd-form-grid--options">
              <label className="abd-check">
                <input
                  type="checkbox"
                  checked={Number(form.is_active) === 1}
                  onChange={(e) => set("is_active", e.target.checked ? 1 : 0)}
                />
                <span>Active (visible to customers at checkout)</span>
              </label>
              <div className="abd-form-field abd-form-field--inline">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.sort_order}
                  onChange={(e) => set("sort_order", e.target.value)}
                  placeholder="0"
                  className="abd-form-field--short"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="abd-modal__footer">
          <button className="abd-btn abd-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="abd-btn abd-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create Account" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ record, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/banking-details/${record.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="abd-modal-overlay" onClick={onClose}>
      <div className="abd-modal abd-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="abd-delete__icon">🏦</div>
        <h2 className="abd-delete__title">Delete Bank Account?</h2>
        <p className="abd-delete__body">
          You are about to permanently delete the{" "}
          <strong>{record?.bank_name}</strong> account for{" "}
          <strong>{record?.account_name}</strong>. This action cannot be undone.
        </p>
        {error && <div className="abd-modal__error">⚠ {error}</div>}
        <div className="abd-delete__actions">
          <button className="abd-btn abd-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="abd-btn abd-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminBankingDetails() {
  const [records,  setRecords]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  /* Filters */
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load ── */
  const loadRecords = useCallback(() => {
    setLoading(true);
    setError("");

    apiFetch("/api/admin/banking-details")
      .then((data) => {
        let rows = Array.isArray(data) ? data : [];

        if (search.trim()) {
          const q = search.trim().toLowerCase();
          rows = rows.filter(
            (r) =>
              r.bank_name?.toLowerCase().includes(q)      ||
              r.account_name?.toLowerCase().includes(q)   ||
              r.account_number?.toLowerCase().includes(q) ||
              r.account_type?.toLowerCase().includes(q)
          );
        }
        if (statusFilter !== "") {
          rows = rows.filter((r) => String(Number(r.is_active)) === statusFilter);
        }

        setRecords(rows);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {}, 400);
  };

  const openModal  = (type, record = null) => { setModal(type); setTarget(record); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadRecords(); };

  const totalActive   = records.filter((r) => Number(r.is_active) === 1).length;
  const totalInactive = records.filter((r) => Number(r.is_active) !== 1).length;

  return (
    <AdminLayout pageTag="SHOP" pageTitle="Banking Details">

      {error && <div className="abd-error-bar">⚠ {error}</div>}

      {/* ── Summary strip ── */}
      <div className="abd-summary">
        <div className="abd-summary__item">
          <span className="abd-summary__value">{loading ? "…" : records.length}</span>
          <span className="abd-summary__label">Shown</span>
        </div>
        <div className="abd-summary__divider" />
        <div className="abd-summary__item">
          <span className="abd-summary__value abd-summary__value--active">{loading ? "…" : totalActive}</span>
          <span className="abd-summary__label">Active</span>
        </div>
        <div className="abd-summary__divider" />
        <div className="abd-summary__item">
          <span className="abd-summary__value abd-summary__value--inactive">{loading ? "…" : totalInactive}</span>
          <span className="abd-summary__label">Inactive</span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="abd-toolbar">
        <div className="abd-toolbar__left">
          <div className="abd-search-wrap">
            <span className="abd-search-wrap__icon">🔍</span>
            <input
              className="abd-search"
              type="text"
              placeholder="Search bank accounts…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button className="abd-search-wrap__clear" onClick={() => setSearch("")}>✕</button>
            )}
          </div>

          <select
            className="abd-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <div className="abd-toolbar__right">
          <button className="abd-btn-primary" onClick={() => openModal("create")}>
            + Add Bank Account
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="abd-table-skeleton" />
      ) : records.length === 0 ? (
        <div className="abd-empty">
          <span>🏦</span>
          <p>No banking details found.</p>
          {(search || statusFilter) && (
            <button
              className="abd-btn abd-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="abd-table-wrap">
          <table className="abd-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Bank</th>
                <th>Account Name</th>
                <th>Account Number</th>
                <th>Branch Code</th>
                <th>Account Type</th>
                <th>Reference Note</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id}>
                  <td className="abd-table__id">{r.id}</td>
                  <td>
                    <div className="abd-bank-cell">
                      <span className="abd-bank-cell__icon">🏦</span>
                      <span className="abd-bank-cell__name">{r.bank_name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="abd-account-name">{r.account_name}</span>
                  </td>
                  <td>
                    <code className="abd-account-number">{r.account_number}</code>
                  </td>
                  <td>
                    {r.branch_code
                      ? <code className="abd-branch-code">{r.branch_code}</code>
                      : <span className="abd-muted">—</span>}
                  </td>
                  <td>
                    {r.account_type
                      ? <span className="abd-type-pill">{r.account_type}</span>
                      : <span className="abd-muted">—</span>}
                  </td>
                  <td>
                    <span className="abd-ref-note">
                      {r.reference_note
                        ? r.reference_note.length > 50
                          ? r.reference_note.slice(0, 50) + "…"
                          : r.reference_note
                        : <span className="abd-muted">—</span>}
                    </span>
                  </td>
                  <td>
                    <span className="abd-order-badge">{r.sort_order ?? 0}</span>
                  </td>
                  <td>
                    <StatusBadge active={r.is_active} />
                  </td>
                  <td>
                    <DotMenu
                      onEdit={()   => openModal("edit",   r)}
                      onDelete={() => openModal("delete", r)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "create" && (
        <FormModal mode="create" onClose={closeModal} onSaved={afterSave} />
      )}
      {modal === "edit" && (
        <FormModal mode="edit" record={target} onClose={closeModal} onSaved={afterSave} />
      )}
      {modal === "delete" && (
        <DeleteModal record={target} onClose={closeModal} onDeleted={afterSave} />
      )}

    </AdminLayout>
  );
}